import { makeAutoObservable } from "mobx";
import { RuleProps } from "./features/RuleRow";
import { ConditionProps } from "./components/ConditionRow";
import {
  ColumnKeyIN,
  ColumnSourceEnum,
  FilteringRule,
  FilteringRuleENUM,
  ProcessingRuleOUT,
  PropertyBrowserNodeOUT,
  processingRulesApi,
  propertyBrowserApi,
} from "api-client";
import { PROCESSING_RULE_OPERATORS } from "features/DynamicTable/consts";
import { EMPTY_CONDITION } from "./features/RuleConditions";

const resolveKey = (columnKey: string, columnSource: string) =>
  `${columnSource}:${columnKey}`;

class RulesSetStore {
  currentRule: RuleProps | undefined = undefined;
  rulesList: RuleProps[] = [];
  organisationID: string | undefined = undefined;
  openNoRulePopup: boolean = false;
  loading: boolean = true;
  inProgressRuleID: number | undefined = undefined;

  setCurrentRule(currentRule: RuleProps | undefined) {
    if (currentRule) {
      this.currentRule = {
        ...currentRule,
        conditions: currentRule.conditions.map((rec) => ({
          ...rec,
          valueProperty: rec.valueProperty
            ? { ...rec.valueProperty }
            : undefined,
          property: rec.property ? { ...rec.property } : undefined,
        })),
      };
    } else {
      this.currentRule = undefined;
    }
  }

  setRulesList(rulesList: RuleProps[]) {
    this.rulesList = rulesList;
  }

  async setRuleConditions(conditions: ConditionProps[]) {
    if (this.currentRule) {
      this.setCurrentRule({ ...this.currentRule, conditions });
    }
  }

  setOrganisationID(organisationID: string | undefined) {
    this.organisationID = organisationID;
  }

  setInProgressRuleID(inProgressRuleID: number | undefined) {
    this.inProgressRuleID = inProgressRuleID;
  }

  setOpenNoRulePopup(openNoRulePopup: boolean) {
    this.openNoRulePopup = openNoRulePopup;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  isThereAnyValidRule() {
    return this.rulesList
      .map((item) => item.conditions)
      .flat()
      .map((rec) => rec.property)
      .some(Boolean);
  }

  reset() {
    this.currentRule = undefined;
    this.rulesList = [];
    this.loading = true;
    this.inProgressRuleID = undefined;
  }

  private transformCondition(
    condition: FilteringRule,
    resolvedNodes: Record<string, PropertyBrowserNodeOUT>
  ) {
    return {
      property:
        condition.column_key && condition.column_source
          ? resolvedNodes[
              resolveKey(condition.column_key, condition.column_source)
            ]
          : undefined,
      operator: PROCESSING_RULE_OPERATORS.find(
        (op) => op.id === condition.operator
      )?.id,
      value:
        condition.value_source === ColumnSourceEnum.Literal
          ? (condition.value as string) ?? ""
          : "",
      valueProperty:
        condition.value_source !== ColumnSourceEnum.Literal &&
        condition.value &&
        condition.value_source
          ? resolvedNodes[
              resolveKey(condition.value as string, condition.value_source)
            ]
          : undefined,
      value_source: condition.value_source,
    };
  }

  private transformRule(
    item: ProcessingRuleOUT,
    resolvedNodes: Record<string, PropertyBrowserNodeOUT>
  ) {
    const rules = item.filter?.rules ?? [];
    return {
      id: item.id,
      name: item.name,
      conditions: rules.length
        ? rules.map((condition) =>
            this.transformCondition(condition, resolvedNodes)
          )
        : [EMPTY_CONDITION],
      validationError: Boolean(
        item.validation_errors && item.validation_errors != "Empty"
      ),
    };
  }

  async fetchRulesList(organisation_id?: string | null) {
    const orgID = organisation_id ?? this.organisationID;
    if (!orgID) return;

    const rulesRes = await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesListProcessingRules(orgID)
      .catch((err) => {
        console.error(err, "ProcessingRulesListProcessingRules");
        return null;
      });
    if (!rulesRes) return;

    const allConditions = rulesRes.data.flatMap((r) => r.filter?.rules ?? []);
    const uniqueKeysMap = new Map<string, ColumnKeyIN>();
    for (const c of allConditions) {
      if (c.column_key && c.column_source) {
        uniqueKeysMap.set(resolveKey(c.column_key, c.column_source), {
          column_key: c.column_key,
          column_source: c.column_source,
        });
      }
      if (
        c.value &&
        c.value_source &&
        c.value_source !== ColumnSourceEnum.Literal
      ) {
        uniqueKeysMap.set(resolveKey(c.value as string, c.value_source), {
          column_key: c.value as string,
          column_source: c.value_source,
        });
      }
    }
    const columnKeys = Array.from(uniqueKeysMap.values());

    let resolvedNodes: Record<string, PropertyBrowserNodeOUT> = {};
    if (columnKeys.length > 0) {
      resolvedNodes = await propertyBrowserApi()
        .mappingEditorApiV1RoutersPropertyBrowserOrganisationResolveNodes(
          orgID,
          { column_keys: columnKeys }
        )
        .then((res) => res.data)
        .catch((err) => {
          console.error(err, "PropertyBrowserResolveNodes");
          return {};
        });
    }

    this.setRulesList(
      rulesRes.data.map((item) => this.transformRule(item, resolvedNodes))
    );
  }

  async createNewRule(rule: RuleProps) {
    if (!this.organisationID) return;
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesCreateAProcessingRule({
        organisation_id: this.organisationID,
        processing_rule: {
          name: rule.name,
          action: "manual_match",
          filter: {
            logical_operator: "AND",
            rules: this.getFilteringRuleList(rule),
          },
        },
      })
      .then(async (res) => {
        this.setInProgressRuleID(res.data.id);
        await this.fetchRulesList();
        this.setCurrentRule(this.rulesList[this.rulesList.length - 1]);
      })
      .catch((err) =>
        console.error(err, "ProcessingRulesCreateAProcessingRule")
      );
  }

  async removeRule(ruleID: number) {
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesDeleteProcessingRule(ruleID)
      .then(async () => {
        await this.fetchRulesList();
        this.setCurrentRule(this.rulesList[this.rulesList.length - 1]);
      })
      .catch((err) =>
        console.error(err, "ProcessingRulesDeleteProcessingRule")
      );
  }

  async updateRule(name?: string) {
    if (!this.currentRule) return;
    const ruleConditions = this.getFilteringRuleList(this.currentRule);
    await processingRulesApi()
      .mappingEditorApiV1RoutersProcessingRulesUpdateProcessingRule(
        this.currentRule.id,
        {
          name: name ?? this.currentRule.name,
          filter: {
            logical_operator: "AND",
            rules: ruleConditions,
          },
          action: "manual_match",
        }
      )
      .catch((err) => console.error(err, "ProcessingRulesUpdateProcessingRule"))
      .finally(async () => {
        await this.fetchRulesList();
        const updated = this.rulesList.find(
          (r) => r.id === this.currentRule?.id
        );
        if (updated) this.setCurrentRule(updated);
      });
  }

  private getFilteringRuleList(rule: RuleProps) {
    return rule.conditions.map((item) => {
      return {
        column_key: item.property?.column_key || null,
        column_source:
          (item.property?.column_source as ColumnSourceEnum) || null,
        operator: item.operator as FilteringRuleENUM,
        value: item.valueProperty
          ? item.valueProperty?.column_key || null
          : item.value || null,
        value_source: item.valueProperty
          ? (item.value_source as ColumnSourceEnum)
          : item.value
          ? ColumnSourceEnum.Literal
          : null,
      };
    });
  }

  async updateRulesOrders() {
    if (this.rulesList.length === 0) return;
    await processingRulesApi().mappingEditorApiV1RoutersProcessingRulesUpdateProcessingRuleOrders(
      this.rulesList.map((item, index) => ({
        processing_rule_id: item.id,
        order: index + 1,
      }))
    );
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: RulesSetStore;

  static getInstance(): RulesSetStore {
    if (!RulesSetStore.instance) {
      RulesSetStore.instance = new RulesSetStore();
    }
    return RulesSetStore.instance;
  }
}

export const rulesSetStore = RulesSetStore.getInstance();
